/**
 * Shallow value-equality for two flat records of primitives.
 *
 * Used by the GameTile React.memo comparators. CashGame and Tournament are
 * both flat objects (string/number/boolean fields, no nesting), so comparing
 * every key by `===` is a complete and correct equality check — and it
 * automatically covers any field added later, unlike a hand-listed comparator.
 *
 * Returns true when both records have the same keys and identical values.
 */
export function shallowEqualRecord<T extends object>(a: T, b: T): boolean {
  if (a === b) return true;

  // `keyof T` cast: interfaces lack an implicit index signature, so Object.keys
  // is typed as string[]; the cast lets us index `a[key]` without widening the
  // parameters to `Record<string, unknown>` (which interfaces don't satisfy).
  const keysA = Object.keys(a) as Array<keyof T>;
  const keysB = Object.keys(b);
  if (keysA.length !== keysB.length) return false;

  for (const key of keysA) {
    if (a[key] !== b[key]) return false;
  }
  return true;
}
